import { useAuth } from '~/auth/Auth'
import { useMutation, useQuery } from '@apollo/client'
import {
  AddMemberDocument,
  CommunitySortEnum,
  GetCommunityDocument,
  GetEventDocument,
  GetEventsAttendingDocument,
  GetMyCommunitiesDocument,
  RegisterEventAttendeeDocument,
} from '~/api/generated/graphql'
import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react'
import { elementClicked } from '~/common/EventLogger'
import ToastComponent from '~/common/ToastComponent'
import { Button, Modal } from 'react-bootstrap'
import { useNavigate } from 'react-router'

export const RegisterEventButton = ({
  eventId,
  joinLivestream,
  isFutureLivestream,
}: {
  eventId?: string
  joinLivestream?: () => void
  isFutureLivestream: boolean
}) => {
  const { isVeevan, authUserId } = useAuth()
  const navigate = useNavigate()
  const [addMemberMutation] = useMutation(AddMemberDocument)
  const [showJoinSuccess, setShowJoinSuccess] = useState<boolean>(false)

  const [registerEventAttendee, { loading: isRegistering }] = useMutation(RegisterEventAttendeeDocument)
  const { data: eventsAttendingData } = useQuery(GetEventsAttendingDocument)
  const { data } = useQuery(GetEventDocument, { variables: { id: eventId ?? '' } })
  const event = data?.event
  const { data: communityData } = useQuery(GetCommunityDocument, {
    variables: { id: event?.communityId ?? '' },
    skip: !event?.communityId,
  })
  const { data: userCommunityData } = useQuery(GetMyCommunitiesDocument, {
    variables: { communityFilter: {}, communitySort: [CommunitySortEnum.NameAsc] },
    notifyOnNetworkStatusChange: true,
  })
  const communityIds = useMemo(
    () => userCommunityData?.currentUser?.communities?.edges.map(e => e?.node?.communityId) || [],
    [userCommunityData]
  )

  const community = communityData?.community
  const isMember = useMemo(
    () => Boolean(communityIds?.some(c => c === community?.communityId)),
    [communityIds, community]
  )
  const handleCloseToast = useCallback(() => setShowJoinSuccess(false), [setShowJoinSuccess])

  const [showEventError, setShowEventError] = useState(false)
  const [showBadVeevanModal, setShowBadVeevanModal] = useState(false)
  const [showJoinCommunityModal, setShowJoinCommunityModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const eventsAttending = useMemo(
    () => eventsAttendingData?.currentUser?.eventsAttending?.edges?.map(edge => edge?.node?.eventId) ?? [],
    [eventsAttendingData]
  )

  const currentlyRunning = useMemo(() => {
    const now = new Date()
    const end = new Date(event?.eventEnd)

    return !isFutureLivestream && now <= end
  }, [event, isFutureLivestream])

  const canRegister = event?.isLivestream && event?.googleCalendar
  const showJoinButton = useMemo(() => {
    return event?.isLivestream && (currentlyRunning || (isVeevan && event?.livestreamStart))
  }, [event, isVeevan, currentlyRunning])

  const isPastLivestream = useMemo(() => {
    const now = new Date()
    const end = new Date(event?.eventEnd)
    return event?.isLivestream && now >= end
  }, [event?.isLivestream, event?.eventEnd])

  const [showInviteSentModal, setShowInviteSentModal] = useState<boolean>(false)

  const isRegistered = event?.eventId && eventsAttending.includes(event.eventId)
  const handleJoinLivestream = useCallback(() => {
    if (joinLivestream) {
      joinLivestream()
    } else {
      void navigate(`/communities/${event?.communityId}/events/${event?.eventId}`, {
        state: { doJoinLivestream: true },
      })
    }
  }, [event, navigate, joinLivestream])

  const handleEventClicked: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e?: SyntheticEvent) => {
      if (isVeevan) {
        setShowBadVeevanModal(true)
      } else if (isRegistered) {
        setShowInviteSentModal(true)
      } else {
        if (e) {
          elementClicked(e, 'click-event-register', { eventId: eventId })
        }
        void registerEventAttendee({
          variables: {
            eventId: eventId ?? '',
          },
        }).then(r => {
          if (r.data?.registerEventAttendee?.error) {
            const errorCode = r?.data.registerEventAttendee?.error?.code
            if (errorCode === 'notInFuture') {
              setErrorMessage('The event has already occurred')
              setShowEventError(true)
            } else if (errorCode === 'notFound') {
              setErrorMessage('The event has been deleted')
              setShowEventError(true)
            } else if (errorCode === 'notASummitLivestream') {
              setErrorMessage('This event has no registration')
              setShowEventError(true)
            } else if (errorCode === 'alreadyRegistered') {
              setErrorMessage('Already registered')
              setShowEventError(true)
            } else if (errorCode === 'veevansMayNotRegister') {
              setErrorMessage('This event is for customers only')
              setShowEventError(true)
            }
          } else {
            if (!isMember) {
              setShowJoinCommunityModal(true)
            }
          }
        })
      }
    },
    [registerEventAttendee, eventId, isMember, isRegistered, isVeevan]
  )

  const handleJoinCommunity = useCallback(async () => {
    try {
      setShowJoinCommunityModal(false)
      await addMemberMutation({
        variables: {
          userId: authUserId ?? '',
          communityId: event?.communityId ?? '',
        },
      })
      setShowJoinSuccess(true)
    } catch {
      console.log(`Something went wrong.`)
    }
  }, [authUserId, event, addMemberMutation])
  return (
    <div className="register">
      {showJoinButton ? (
        <button
          data-testid={`join-btn ${event?.eventId}`}
          className={'join-btn btn-plain'}
          onClick={handleJoinLivestream}
        >
          <div className={`btn-title`} />
          <span>Join Live!</span>
        </button>
      ) : isPastLivestream ? (
        <span>Livestream Concluded</span>
      ) : canRegister ? (
        <button
          data-testid={`register-btn ${event?.eventId}`}
          className={`btn-plain ${isRegistered && 'registered'}`}
          onClick={handleEventClicked}
          disabled={isRegistering}
        >
          <div className={`btn-title`} />
          <span>{isRegistering ? 'Registering...' : isRegistered ? 'Invite sent!' : 'Register for Livestream'}</span>
        </button>
      ) : (
        ''
      )}
      <ToastComponent
        bg={'danger'}
        show={showEventError}
        onClose={() => {
          setShowEventError(false)
        }}
      >
        {errorMessage}
      </ToastComponent>
      <Modal show={showInviteSentModal} onHide={() => setShowInviteSentModal(false)}>
        <Modal.Body>
          <p>Invite Sent!</p>
          <p>Please check your email for an invitation from {event?.googleCalendar}.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'primary'} onClick={() => setShowInviteSentModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showJoinCommunityModal} onHide={() => setShowJoinCommunityModal(false)}>
        <Modal.Body>
          <p>Would you like to join the {community?.name} community?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'link'} onClick={() => setShowJoinCommunityModal(false)}>
            No Thanks
          </Button>
          <Button variant={'primary'} onClick={() => handleJoinCommunity()}>
            Join Community
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showBadVeevanModal} onHide={() => setShowBadVeevanModal(false)}>
        <Modal.Body>
          <p>Livestreams are for customers only, please watch the replay.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'primary'} onClick={() => setShowBadVeevanModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastComponent show={showJoinSuccess} onClose={handleCloseToast}>
        {`You've joined ${community?.name}.`}
      </ToastComponent>
    </div>
  )
}
